import {
  ensureObject,
  ensureDependency,
  ensureMethod,
  ensureEvent,
  ensureFSM,
  ensureTransition,
  ensureMPC,
  ensureState,
  ensureInheritance,
} from '../util';
import {
  ADMC_Object,
  ADMC_Event,
  ADMC_Attribute,
  ADMC_Method,
  ADMC_Dependency,
  ADMC_Transition,
  ADMC_State,
  ADMC_FSM,
  ADMC_MultiplePropagationConstraint,
  ADMC_Inheritance,
} from '../atomic';
import {
  Combined_DeleteDependency,
  Combined_DeleteEvent,
  Combined_DeleteTransition,
  Combined_DeleteFSM,
  Combined_DeleteInheritance,
  Combined_ConvertToNormalEvent,
} from '../../combined';
import { CEMC_Transition, CEMC_FSM } from './edit';
import { CEMC_ConvertToOwnedMethod, CEMC_Object } from '..';
import type { IModel } from '../../types';
import type { AnyCheck, IdOptionMap, OrId } from '../../types';
import type { IObjectType } from '../../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../../stores/model/metamodel/method';
import type { IDependency } from '../../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../../stores/model/metamodel/inheritance';
import type { IFSM } from '../../../../stores/model/metamodel/fsm';
import type { IState } from '../../../../stores/model/metamodel/state';
import type { ITransition } from '../../../../stores/model/metamodel/transition';
import type { IMultiplePropagationConstraint } from '../../../../stores/model/metamodel/multiple_propagation_constraint';

/**
 * Delete an object type (including all its attributes), all methods that
 * reference it and all dependencies that reference it (using
 * Combined_DeleteDependency).
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type or its id.
 * @param {boolean} removeEvents Also delete related events.
 * @param {Map} inheritanceOptions Options to pass down when removing inheritances. Key is the id of
 *  the inheritance relation. See CDMC_Inheritance.
 */
export function CDMC_Object(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  removeEvents: boolean,
  inheritanceOptions: Record<string, IdOptionMap | undefined>
) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const object = ensureObject(metamodel, objectOrId)!;
  const name = object.name;

  // Delete the inheritances
  for (const inh of object.inheritances.slice()) {
    const { checks: extraChecks } = Combined_DeleteInheritance(
      model,
      inh,
      inheritanceOptions[inh.id],
      // don't check the supertype abstract state, if we are going to delete is soon.
      inh.supertype !== object
    );
    checks.push(...extraChecks);
  }

  // Delete dependencies
  for (const dep of object.dependencies) {
    const { checks: extraChecks } = Combined_DeleteDependency(model, dep);
    checks.push(...extraChecks);
  }

  // Delete attributes
  // Slice because the array will be modified!
  for (const attr of object.attributes.slice()) {
    ADMC_Attribute(object, attr);
    // No checks needed
  }

  // Delete methods
  for (const m of object.methods) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  // Delete the FSMs
  const defaultFSM = object.defaultFSM;
  // Slice because the array will be modified!
  // First remove all non-default FSMs
  for (const fsm of object.fsms.slice()) {
    if (fsm !== defaultFSM) {
      const { checks: extraChecks } = Combined_DeleteFSM(model, object, fsm);
      checks.push(...extraChecks);
    }
  }
  // Now remove the default FSM
  if (defaultFSM != null) {
    const { checks: extraChecks } = Combined_DeleteFSM(model, object, defaultFSM);
    checks.push(...extraChecks);
  }

  // Delete the actual object
  ADMC_Object(model.metamodel, object);
  // No additional checks are needed

  // Delete related events if requested
  if (removeEvents) {
    const eventsToDelete = metamodel.eventTypes.filter((e) => {
      // Delete events that:
      // 1. have the name of the delete object in their name
      // 2. have no owning object type anymore
      return e.name.includes(name) && e.owningObjectTypes.length === 0;
    });
    for (const e of eventsToDelete) {
      const { checks: extraChecks } = Combined_DeleteEvent(model, e);
      checks.push(...extraChecks);
    }
  }

  return { checks };
}

/**
 * Delete a dependency and all methods that depend on it.
 *
 * @param {Model} model The model to operate on.
 * @param {Dependency or String} depOrId The dependency or its id.
 */
export function CDMC_Dependency(model: IModel, depOrId: OrId<IDependency>) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const dependency = ensureDependency(metamodel, depOrId)!;

  // Delete methods that depend on this dependency
  for (const m of dependency.findAcquiredMethods()) {
    // CDMC_Method will propagate the delete further up the dependency chain
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  // Delete multiple propagation constraints
  for (const mpc of dependency.dependentMultiplePropagationConstraints) {
    CDMC_MultiplePropagationConstraint(model, mpc.objectType, mpc);
  }

  // Delete the actual dependency
  ADMC_Dependency(metamodel, dependency);

  return { checks };
}

/**
 * Delete a method (including all its attributes) and all methods that depend on
 * it.
 *
 * @param {Model} model The model to operate on.
 * @param {Method or String} methodOrId The method
 */
export function CDMC_Method(model: IModel, methodOrId: OrId<IMethod>) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const method = ensureMethod(metamodel, methodOrId)!;

  // Delete attributes
  // Slice because array will be modified
  for (const attr of method.attributes.slice()) {
    ADMC_Attribute(method, attr);
    // No checks needed
  }

  // Delete transitions
  for (const t of method.transitions.slice()) {
    const transitionMethods = t.metaMethods.slice();
    const i = transitionMethods.indexOf(method);
    if (method.provenance === 'SPECIALISED') {
      // A specialised method always has a viaMethod.
      transitionMethods.splice(i, 1, method.viaMethod!);
    } else {
      transitionMethods.splice(i, 1);
    }
    if (transitionMethods.length === 0) {
      const { checks: extraChecks } = Combined_DeleteTransition(
        model,
        method.ownerObjectType,
        t.fsm,
        t
      );
      checks.push(...extraChecks);
    } else {
      const { checks: extraChecks } = CEMC_Transition(
        model,
        method.ownerObjectType,
        t.fsm,
        t,
        transitionMethods
      );
      checks.push(...extraChecks);
    }
  }

  // Delete acquired methods
  for (const m of method.findAcquiredMethods()) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  for (const m of method.findInheritedMethods()) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  if (method.inherited) {
    for (const m of method.findSpecialisedMethods()) {
      const { checks: extraChecks } = CDMC_Method(model, m);
      checks.push(...extraChecks);
    }
  }

  // Delete the actual method
  ADMC_Method(metamodel, method);
  // No additional checks are needed

  return { checks };
}

/**
 * Delete an event type (including all its attribute) and all methods that
 * depend on it.
 *
 * @param {Model} model The model to operate on.
 * @param {EventType or String} eventOrId The event type.
 */
export function CDMC_Event(model: IModel, eventOrId: OrId<IEventType>) {
  const metamodel = model.metamodel;
  const checks: AnyCheck[] = [];

  const event = ensureEvent(metamodel, eventOrId)!;

  // Delete attributes
  // Slice because array will be modified
  for (const attr of event.attributes.slice()) {
    ADMC_Attribute(event, attr);
    // No checks needed
  }

  // Delete methods (only owned and specialised, others are deleted by propagation)
  for (const m of event.ownedMethods.concat(event.specialisedMethods)) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  // Delete specialisations
  for (const e of event.specialisations.slice()) {
    const { checks: extraChecks } = Combined_DeleteEvent(model, e);
    checks.push(...extraChecks);
  }

  // Delete the actual event
  ADMC_Event(metamodel, event);
  // No additional checks are needed

  return { checks };
}

/**
 * Delete the given multiple propagation constraint from the given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object the remove the MPC from.
 * @param {MultiplePropagationConstraint or String} mpcOrId The MPC to remove.
 */
export function CDMC_MultiplePropagationConstraint(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  mpcOrId: OrId<IMultiplePropagationConstraint>
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const mpc = ensureMPC(object, mpcOrId)!;

  ADMC_MultiplePropagationConstraint(object, mpc);

  return { checks };
}

/**
 * Delete the given FSM from the given object type.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object the remove the FSM from.
 * @param {FSM or String} fsmOrId The FSM to remove.
 */
export function CDMC_FSM(model: IModel, objectOrId: OrId<IObjectType>, fsmOrId: OrId<IFSM>) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const isSelected = fsm.codegeneration;
  const isDefault = fsm.type === 'DEFAULT';

  // Delete transitions
  // Slice because the array will be modified
  for (const trans of fsm.transitions.slice()) {
    const { checks: extraChecks } = CDMC_Transition(model, object, fsm, trans);
    checks.push(...extraChecks);
  }

  // Delete states
  // Slice because the array will be modified
  for (const state of fsm.states.slice()) {
    ADMC_State(fsm, state);
  }

  // Delete the FSM
  ADMC_FSM(object, fsm);

  // Select the default FSM for code generation if the FSM we are deleting is
  // selected for code generation
  if (!isDefault && isSelected) {
    const { checks: selectChecks } = CEMC_FSM(model, object, object.defaultFSM!, {
      codegeneration: true,
    });
    checks.push(...selectChecks);
  }

  return { checks };
}

/**
 * Delete the given state from the given FSM.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object.
 * @param {FSM or String} fsmOrId The FSM.
 * @param {State or String} stateOrId The state to remove.
 */
export function CDMC_State(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  stateOrId: OrId<IState>
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const state = ensureState(fsm, stateOrId)!;

  // Remove the transitions
  for (const transition of state.transitions) {
    const { checks: extraChecks } = Combined_DeleteTransition(model, object, fsm, transition);
    checks.push(...extraChecks);
  }

  // Delete the state
  ADMC_State(fsm, state);
  // No additional checks needed

  return { checks };
}

/**
 * Delete the given transition from the given FSM.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object.
 * @param {FSM or String} fsmOrId The FSM.
 * @param {Transition or String} transitionOrId The transition to remove.
 */
export function CDMC_Transition(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  transitionOrId: OrId<ITransition>
) {
  const checks: AnyCheck[] = [];

  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const transition = ensureTransition(fsm, transitionOrId)!;

  ADMC_Transition(fsm, transition);

  // This can introduce forward or backward inaccessible states and can leave
  // events unused. However, those checks don't generate errors, so it's no use
  // running them here. They won't be shown to the user anyway.

  return { checks };
}

/**
 * Delete an inheritance relation and its EDG link.
 *
 * @param {Model} model The model to operate on.
 * @param {Inheritance or String} inhOrId The inheritance.
 * @param {Map} specialisedEventOptions Information about what to do with the specialised events
 * specialised along the inheritance. This is a map of the id of the specialised event to one of:
 * - KEEP: keep the specialised event, but convert it to a normal event
 * - DELETE: delete the specialised event
 * @param {Boolean} checkSupertype check if the supertype needs to be made non-abstract?
 */
export function CDMC_Inheritance(
  model: IModel,
  inhOrId: OrId<IInheritance>,
  specialisedEventOptions: IdOptionMap | null | undefined,
  checkSupertype = true
) {
  const checks: AnyCheck[] = [];

  const inheritance = ensureInheritance(model.metamodel, inhOrId)!;

  // 1. Remove all acquired methods that were propagated via the inheritance
  const methodsToRemove = model.metamodel.acquiredMethods.filter((am) => {
    return (
      am.propagationPath!.includes(inheritance) &&
      // if the viaMethod's propagation path includes the inheritance, it will be deleted through
      // propagation
      !am.viaMethod!.propagationPath!.includes(inheritance)
    );
  });
  for (const m of methodsToRemove) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  // 2. Remove or convert all specialised events (+ specialised methods) that depend on this
  //    inheritance
  const specialisedEventsToConsider = inheritance.specialisedEvents.slice();
  for (const event of specialisedEventsToConsider) {
    const subtypeHasSpecialisedMethod = inheritance.subtype.specialisedMethods.some(
      (m) => m.ownerEventType === event
    );
    if (
      !subtypeHasSpecialisedMethod ||
      specialisedEventOptions == null ||
      specialisedEventOptions[event.id] !== 'KEEP'
    ) {
      // remove the specialised event
      const { checks: extraChecks } = Combined_DeleteEvent(model, event);
      checks.push(...extraChecks);
    } else {
      // convert the specialised event and the specialised method to a normal event and an owned
      // method -> This is the inverse of what happened in CCMC_Inheritance. Same remark about
      // creating a new event/method vs converting stays valid.

      // There should be at most 1 specialised method...
      const method = event.specialisedMethods.find(
        (m) => m.ownerObjectType === inheritance.subtype
      );
      if (method != null) {
        const { checks: extraChecks } = CEMC_ConvertToOwnedMethod(model, method);
        checks.push(...extraChecks);
      }

      // Now convert to event back to a normal event
      const { checks: extraChecks } = Combined_ConvertToNormalEvent(model, event);
      checks.push(...extraChecks);
    }
  }

  // 3. Remove all inherited methods in the subtype
  const inheritedMethodsToRemove = inheritance.subtype.inheritedMethods.filter(
    // If the viaMethod's propagation path includes the inheritance, the method will be removed via propagation
    (m) => !m.viaMethod!.propagationPath!.includes(inheritance)
  );
  for (const m of inheritedMethodsToRemove) {
    const { checks: extraChecks } = CDMC_Method(model, m);
    checks.push(...extraChecks);
  }

  // 4. Remove all MPC's that pass through the inheritance
  for (const mpc of inheritance.dependentMultiplePropagationConstraints.slice()) {
    const { checks: extraChecks } = CDMC_MultiplePropagationConstraint(model, mpc.objectType, mpc);
    checks.push(...extraChecks);
  }

  // 5. Mark supertype as non-abstract if applicable
  if (checkSupertype) {
    const supertype = inheritance.supertype;
    if (supertype.abstract && supertype.inheritancesAsSupertype.length === 1) {
      // We are about to remove the last inheritance as supertype of an abstract object -> mark it
      // non-abstract
      const { checks: extraChecks } = CEMC_Object(model, supertype, { abstract: false }, false);
      checks.push(...extraChecks);
    }
  }

  // 6. Remove the inheritance itself
  ADMC_Inheritance(model.metamodel, inheritance);
  // No additional checks needed

  return {
    checks,
  };
}
